import { useEffect, useRef } from 'react';
import { apiClient } from '@api/client';
import { config } from '@shared/config';
import { useConnectivityStore } from './useConnectivityStore';
import type { ConnectivityState } from '@shared/types';

const HEARTBEAT_TIMEOUT_MS = 15_000;
const HEALTH_POLL_INTERVAL_MS = 10_000;

interface HealthResponse {
  omsConnected: boolean;
}

/**
 * Detects connectivity state from health endpoint polling.
 * Updates the global connectivity store.
 *
 * In production this would also listen to SignalR heartbeats;
 * here we use periodic health checks as the primary mechanism.
 */
export function useConnectivityDetection() {
  const setState = useConnectivityStore((s) => s.setState);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    // In dev mode, allow forcing permanent online state via env variable
    if (config.forceOnline) {
      setState('online');
      return;
    }

    let mounted = true;

    async function probe() {
      try {
        const health = await apiClient.getAbsolute<HealthResponse>('/health');
        if (!mounted) return;
        const next: ConnectivityState = health.omsConnected ? 'online' : 'offline';
        console.log('[ConnectivityDetection] Health check succeeded:', {
          omsConnected: health.omsConnected,
          next,
        });
        setState(next);
      } catch (error) {
        if (!mounted) return;
        console.error('[ConnectivityDetection] Health check failed, setting disconnected:', error);
        setState('disconnected');
      }
    }

    // Initial probe
    probe();

    // Periodic polling
    timerRef.current = setInterval(probe, HEALTH_POLL_INTERVAL_MS);

    return () => {
      mounted = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [setState]);
}

// Allow devtools testing: window.__SET_CONNECTIVITY__('offline')
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__SET_CONNECTIVITY__ = (
    state: ConnectivityState,
  ) => {
    useConnectivityStore.getState().setState(state);
  };
}

// Suppress unused export for heartbeat timeout constant
void HEARTBEAT_TIMEOUT_MS;
